import { DashboardLoginPage } from "@dashboard/components/DashboardLoginPage"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { dashboardAppHostRedirectUrl } from "@utils/dashboardHost"
import { currentRequestHostname } from "@utils/dashboardHostRuntime"

type LoginSearch = {
  returnTo?: string
  error?: string
  success?: string
  code?: string
  message?: string
  email?: string
  email_verified?: string
  verification?: string
}

export const Route = createFileRoute("/login")({
  validateSearch: (search): LoginSearch => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    success: typeof search.success === "string" ? search.success : undefined,
    code: typeof search.code === "string" ? search.code : undefined,
    message: typeof search.message === "string" ? search.message : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
    email_verified: typeof search.email_verified === "string" ? search.email_verified : undefined,
    verification: typeof search.verification === "string" ? search.verification : undefined,
  }),
  beforeLoad: async ({ location }) => {
    const redirectHref = dashboardAppHostRedirectUrl(await currentRequestHostname(), location.href)
    if (redirectHref) throw redirect({ href: redirectHref })
  },
  component: LoginRoute,
})

function LoginRoute() {
  const search = Route.useSearch()
  const emailVerification = readLoginEmailVerification(search)
  return (
    <DashboardLoginPage
      returnTo={search.returnTo}
      routeError={search.error}
      verification={emailVerification?.verification}
      verificationEmail={emailVerification?.email}
      verificationMessage={emailVerification?.message}
    />
  )
}

function readLoginEmailVerification(search: LoginSearch) {
  const success = search.success?.toLowerCase()
  const code = search.code?.toLowerCase()
  const emailVerified = search.email_verified?.toLowerCase()
  const verification = search.verification?.toLowerCase()
  const message = search.message?.trim()
  const looksVerified = message ? /verified/i.test(message) : false

  if (
    emailVerified === "true" ||
    verification === "success" ||
    (success === "true" && (code === "success" || looksVerified))
  ) {
    return {
      verification: "success" as const,
      email: search.email,
      message: message || "Your email address has been verified.",
    }
  }

  return null
}
